// planRowGroups — helpers for the PlanPanel route list:
//   - skip rows flagged `hiddenInPlanList` (synthesized altitude-terminator
//     legs that exist only to drive map-geometry, NOT user-visible plan
//     rows)
//   - identify the first / last row of each contiguous procedure block so
//     the panel can paint a vertical bracket
//   - render a "procedure header" label (e.g. "FOBRO1 Departure",
//     "I29R Approach", "I29R Missed") at the start of each contiguous
//     procedure block
//
// Extracted here from PlanPanel.tsx so the logic is unit-testable. See
// Bug #4 (2026-05-16): the boss wanted contiguous SID/STAR/IAP fixes
// rendered under a labeled group header instead of as anonymous rows
// with a colored left bracket.

export type RouteType = 'SID' | 'STAR' | 'IAP';

export interface PlanRowProcedureTag {
  airportIcao?: string;
  procIdent?: string;
  routeType?: RouteType;
  isMissedApproach?: boolean;
  hiddenInPlanList?: boolean;
  placeholder?: boolean;
  synthetic?: boolean;
}

export interface PlanRowLike {
  procedureTag?: PlanRowProcedureTag;
}

export interface ProcGroupFlags {
  /** True if this row belongs to a contiguous procedure block (SID/STAR/IAP). */
  inGroup: boolean;
  /** True if this is the first row of a contiguous procedure block. */
  isFirstInGroup: boolean;
  /** True if this is the last row of a contiguous procedure block. */
  isLastInGroup: boolean;
  /** Procedure routeType for this row (null if not a procedure row). */
  routeType: RouteType | null;
  /** Procedure ident (e.g. "FOBRO1") for this row. */
  procIdent: string | null;
  /** Header label to show ABOVE this row when isFirstInGroup is true.
   *  Examples: "FOBRO1 Departure", "ALWYS3 Arrival",
   *  "I29R Approach", "I29R Missed". null when not a header row. */
  headerLabel: string | null;
}

/** Compute group flags + header labels for every row in the route.
 *  A "contiguous block" is broken whenever the (procIdent, routeType,
 *  isMissedApproach-bool) tuple changes. That way an IAP's inbound and
 *  missed-approach halves get separate headers ("I29R Approach" vs.
 *  "I29R Missed") even though both rows carry the same procIdent. */
export function computeProcGroups<R extends PlanRowLike>(route: R[]): ProcGroupFlags[] {
  function keyOf(r: R | undefined): string | null {
    if (!r) return null;
    const t = r.procedureTag;
    if (!t || !t.routeType) return null;
    return `${t.procIdent ?? ''}|${t.routeType}|${t.isMissedApproach ? 'M' : 'I'}`;
  }

  const out: ProcGroupFlags[] = [];
  for (let i = 0; i < route.length; i++) {
    const r = route[i];
    const t = r.procedureTag;
    const key = keyOf(r);
    const prevKey = keyOf(route[i - 1]);
    const nextKey = keyOf(route[i + 1]);
    const inGroup = key !== null;
    const isFirstInGroup = inGroup && key !== prevKey;
    const isLastInGroup  = inGroup && key !== nextKey;
    let headerLabel: string | null = null;
    if (isFirstInGroup && t) {
      headerLabel = formatProcedureHeader(t);
    }
    out.push({
      inGroup,
      isFirstInGroup,
      isLastInGroup,
      routeType: (t?.routeType ?? null) as RouteType | null,
      procIdent: t?.procIdent ?? null,
      headerLabel,
    });
  }
  return out;
}

/** Format the human-readable label for a procedure-group header row. */
export function formatProcedureHeader(tag: PlanRowProcedureTag): string {
  const ident = tag.procIdent ?? '';
  if (!tag.routeType) return ident.trim();
  switch (tag.routeType) {
    case 'SID':
      return `${ident} Departure`.trim();
    case 'STAR':
      return `${ident} Arrival`.trim();
    case 'IAP':
      return tag.isMissedApproach
        ? `${ident} Missed`.trim()
        : `${ident} Approach`.trim();
  }
}

/** True when a row should be hidden from the PlanPanel list. Currently:
 *  synthesized altitude-terminator legs that exist only as map-geometry
 *  helpers (see Bug #1/#2/#5, 2026-05-16). The map still renders them. */
export function isHiddenInPlanList<R extends PlanRowLike>(row: R): boolean {
  return row.procedureTag?.hiddenInPlanList === true;
}
